// @ts-check
import { debounce } from '../utilities/debounce';

/**
 * @param {string} postcode
 *
 * @return {Promise<Error|{success: boolean; data: string|PostcodeResponse[]}>}
 */
async function request_addresses( postcode ) {
    const res = await fetchPost( `${WPObj.ajaxurl}`, {
        action: 'nu_query_postcode',
        postcode,
        _ajax_nonce: WPObj.ajax_nonce_postcode
    } );

    if ( !res.ok ) {
        if ( res.status === 403 ) {
            return new Error( 'Please try refreshing the page' );
        } else {
            return new Error( 'Something went wrong. Please enter address manually.' );
        }
    };

    const resJson = await res.json();

    return resJson;
}



/**
 * @param {string} address_id
 *
 * @return {Promise<Error|{success: boolean; data: string|AddressResponse}>}
 */
async function request_address( address_id ) {
    const res = await fetchPost( WPObj.ajaxurl, {
        action: 'nu_query_address',
        address_id,
        _ajax_nonce: WPObj.ajax_nonce_address
    } );

    if ( !res.ok ) {
        if ( res.status === 403 ) {
            return new Error( 'Please try refreshing the page' );
        } else {
            console.dir( res );
            return new Error( 'Something went wrong. Please enter address manually.' );
        }
    };

    const resJson = await res.json();

    if ( resJson.success !== true ) {
        return new Error( resJson.data );
    }

    return resJson;
}



/**
 * A fetch wrapper to encode json data as xxx-form-encoded or whatever it's called
 *
 * @param {string} url
 * @param {object} data
 * */
async function fetchPost( url, data ) {
    const body = new FormData();
    Object.entries( data ).forEach( ( [key, value] ) => {
        body.append( key, value );
    } );

    return fetch( url, {
        method: 'POST',
        credentials: 'same-origin',
        body
    } );
}


const postcodeRegex = '[a-zA-Z]{1,2}\\d[a-zA-Z\\d]? ?\\d[a-zA-Z]{2}';
const template = document.createElement( 'template' );
template.innerHTML = `
        <style>
            address-lookup {
                display: grid;
                gap: 0.5rem;
            }
            address-lookup [data-errors]:empty, address-lookup select:empty {
                display: none;
            }
            address-lookup [data-errors] {
                margin: 0;
                line-height: 1;
            }

            address-lookup.loading {
                opacity: 0.5;
                pointer-events: none;
            }
        </style>
        <input type="text" placeholder="Postcode" pattern="${postcodeRegex}" data-postcode>
        <select data-address-select></select>
        <input class="hide" type="text" placeholder="Address" data-fallback>
        <p data-errors></p>
    `;


class HTMLAddressLookupElement extends HTMLElement {
    /**
     * A number, or a string containing a number.
     * @typedef {{address: string, url: string, id: string}[]} PostcodeResponse
     * @typedef {{
        * latitude: string,
        * longitude: string,
        * postcode: string,
        * formatted_address: string[]
     * }} AddressResponse
     *
     */

    static template = template;

    constructor() {
        super();
        this.appendChild( HTMLAddressLookupElement.template.content.cloneNode( true ) );

        this.input = /** @type {HTMLInputElement} */ ( this.querySelector( '[data-postcode]' ) );
        this.select = /** @type {HTMLSelectElement} */ ( this.querySelector( '[data-address-select]' ) );
        this.fallback = /** @type {HTMLInputElement} */( this.querySelector( '[data-fallback]' ) );
        this.errors = /** @type {HTMLParagraphElement} */( this.querySelector( '[data-errors]' ) );
    }

    connectedCallback() {
        this.input.addEventListener( 'input', debounce( this.requestOptionsUpdate.bind( this ), 250 ) );
        this.select.addEventListener( 'input', this.requestLocationUpdate.bind( this ) );
        this.fallback.addEventListener( 'input', () => this.updateAddress( this.fallback ) );

        this.initialiseInputs();
    }

    initialiseInputs() {
        const required = ( this?.dataset?.required ?? false ) === 'true';
        const name = this?.dataset?.name ?? 'address';

        this.input.name = name + '-postcode';

        if ( required ) {
            this.input.setAttribute( 'required', '' );
            this.select.setAttribute( 'required', '' );
            this.fallback.setAttribute( 'required', '' );
        }

        this._value = document.createElement( 'input' );
        this._value.type = 'hidden';
        this._value.name = name;

        this._value_lat = document.createElement( 'input' );
        this._value_lat.type = 'hidden';
        this._value_lat.name = name + '-lat';

        this._value_long = document.createElement( 'input' );
        this._value_long.type = 'hidden';
        this._value_long.name = name + '-long';

        this.appendChild( this._value );
        this.appendChild( this._value_lat );
        this.appendChild( this._value_long );
    }

    disconnectedCallback() { }

    updateAddress( input ) {
        this._value.value = input.value;
    }

    async requestLocationUpdate() {
        if ( !this.select.value ) return;
        this.clearLocation();

        this.classList.add( 'loading' );
        const address = await this.fetchAddress( this.select.value );
        this.classList.remove( 'loading' );

        if ( address instanceof Error ) {
            this.displayError( address.message );
            return;
        };

        this.setLocation( address );
    }

    async requestOptionsUpdate() {
        // If the postcode is not valid, don't bother making a request
        if ( !this.input?.checkValidity() || this.input.value === '' ) {
            return this.displayError( 'Please enter a valid UK Postcode' );
        };

        this.classList.add( 'loading' );
        const options = await this.fetchAddresses();
        this.classList.remove( 'loading' );

        // Error handling
        if ( options instanceof Error ) {
            this.clearLocation();
            this.showFallback();
            this.displayError( options.message );
            return;
        };
        if ( options.length === 0 ) {
            this.clearLocation();
            this.showFallback();
            this.displayError( 'There were no results for the given postcode. Please enter your address manually.' );
            return;
        }

        this.hideFallback();
        this.clearError();
        this.updateAddresses( options );
    }

    /**
     *
     * @param {PostcodeResponse} options
     */
    updateAddresses( options ) {
        this.select.innerHTML = `<option value="">-- Please select your address --</option>
        ${options.map( address =>
            `<option value="${address.id}">${address.address}</option>`
        ).join( '' )}`;
    }

    /**
     * @returns {Promise<Error|PostcodeResponse>}
     */
    async fetchAddresses() {
        try {
            const res = await request_addresses( this.input.value );

            if ( res instanceof Error ) {
                return res;
            } else if ( !res.success ) {
                return new Error( res.data );
            }

            return res.data;
        } catch ( er ) {
            return new Error( 'We\'re unable to lookup your address right now' );
        }
    }

    /**
     * @param {string} id
     * @returns {Promise<Error|AddressResponse>}
     */
    async fetchAddress( id ) {
        try {
            const res = await request_address( this.select.value );

            if ( res instanceof Error ) {
                return res;
            } else if ( !res.success ) {
                return new Error( res.data );
            }

            return res.data;
        } catch ( er ) {
            return new Error( 'We\'re unable to lookup your address right now' );
        }
    }

    /**
     * @param {AddressResponse} data
     */
    setLocation( data ) {
        this._value.value = data.formatted_address.filter( item => item !== '' ).join( ' ' ) + ' ' + data.postcode + ' United Kingdom';
        this._value_lat.value = data.latitude;
        this._value_long.value = data.longitude;

    }

    clearLocation() {
        this._value.value = '';
        this._value_lat.value = '';
        this._value_long.value = '';
    }


    displayError( message ) {
        this.errors.textContent = message;
    }

    clearError() {
        this.errors.textContent = '';
    }

    showFallback() {
        this.fallback.classList.remove( 'hide' );
    }

    hideFallback() {
        this.fallback.classList.add( 'hide' );
    }
}

customElements.define( 'address-lookup', HTMLAddressLookupElement );